/**
 * Command Center shared stylesheet.
 * Colors + tokens intentionally mirror /pages/AskStewieLanding.jsx.
 */
export const commandCenterStyles = `
.cc-root {
  min-height: 100vh;
  background: #0d1438;
  color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif;
  padding-bottom: 90px;
  overflow-x: hidden;
}
.cc-root, .cc-root * { box-sizing: border-box; }

/* ── App header ───────────────────────────────────────────────────── */
.cc-header {
  position: sticky; top: 0; z-index: 40;
  background: rgba(5, 10, 31, 0.92);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
}
.cc-header-inner {
  max-width: 1200px; margin: 0 auto;
  padding: 12px 16px;
  display: flex; align-items: center; gap: 12px;
}
.cc-header-back {
  background: rgba(148,163,184,0.08);
  border: 1px solid rgba(148,163,184,0.20);
  color: #cbd5e1;
  border-radius: 10px;
  padding: 7px 10px;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600;
  font-family: inherit;
  transition: background 160ms, color 160ms, border-color 160ms;
}
.cc-header-back:hover { background: rgba(148,163,184,0.16); color: #f1f5f9; border-color: rgba(148,163,184,0.35); }
.cc-header-logo {
  display: inline-flex; align-items: center; gap: 10px;
  color: #f1f5f9; font-weight: 800; letter-spacing: -0.01em;
  font-size: 16px;
  background: transparent; border: none; cursor: pointer;
  padding: 4px 8px; border-radius: 10px;
  font-family: inherit;
}
.cc-header-logo:hover { background: rgba(255,255,255,0.05); }
.cc-header-logo img { width: 28px; height: 28px; border-radius: 8px; }
.cc-header-logo-text {
  background: linear-gradient(90deg, #67e8f9, #22d3ee);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; color: transparent;
}
.cc-header-spacer { flex: 1; }
.cc-header-user {
  color: #94a3b8; font-size: 13px; font-weight: 500;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
}
.cc-header-signout {
  padding: 7px 12px;
  border-radius: 10px;
  border: 1px solid rgba(220, 38, 38, 0.35);
  color: #fca5a5;
  background: rgba(220, 38, 38, 0.06);
  font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: background 160ms, border-color 160ms;
}
.cc-header-signout:hover { background: rgba(220, 38, 38, 0.14); border-color: rgba(220, 38, 38, 0.55); }
@media (max-width: 640px) {
  .cc-header-user { display: none; }
}

/* ── Main container ──────────────────────────────────────────────── */
.cc-main {
  max-width: 1200px; margin: 0 auto;
  padding: 20px 16px 24px;
  display: flex; flex-direction: column;
  gap: 26px;
}

/* ── Hero ─────────────────────────────────────────────────────────── */
.cc-hero {
  position: relative;
  padding: 22px 20px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(15,27,70,0.88) 0%, rgba(5,10,31,0.92) 100%);
  border: 1px solid rgba(148,163,184,0.20);
  box-shadow: 0 20px 40px rgba(0,0,0,0.35);
  overflow: hidden;
  display: flex; gap: 20px; align-items: center; flex-wrap: wrap;
}
.cc-hero::before {
  content: '';
  position: absolute; inset: 0;
  background:
    radial-gradient(600px 300px at 90% 20%, rgba(103, 232, 249, 0.10), transparent 65%),
    radial-gradient(600px 300px at 10% 100%, rgba(250, 204, 21, 0.06), transparent 65%);
  pointer-events: none;
}
.cc-hero-content { position: relative; flex: 1; min-width: 260px; }
.cc-hero-eyebrow {
  color: #facc15; font-size: 11px; font-weight: 800;
  letter-spacing: 0.22em; text-transform: uppercase;
}
.cc-hero-title {
  margin: 6px 0 0;
  font-size: clamp(24px, 4vw, 34px);
  font-weight: 800; letter-spacing: -0.01em;
  color: #ffffff; line-height: 1.15;
}
.cc-hero-title-name { color: #facc15; }
.cc-hero-sub {
  margin: 10px 0 16px;
  color: #cbd5e1; font-size: 14px; line-height: 1.5;
  max-width: 640px;
}
.cc-hero-cta {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px;
  background: #facc15; color: #0d1438;
  font-size: 14px; font-weight: 800; letter-spacing: 0.02em;
  border-radius: 12px;
  border: none; cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  box-shadow: 0 6px 18px rgba(250, 204, 21, 0.28);
  transition: transform 160ms, box-shadow 160ms, background 160ms;
}
.cc-hero-cta:hover { background: #fde047; transform: translateY(-1px); box-shadow: 0 10px 24px rgba(250, 204, 21, 0.42); }
.cc-hero-avatar {
  position: relative; flex-shrink: 0;
  width: 100px; height: 100px;
  border-radius: 24px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(148,163,184,0.25);
  display: grid; place-items: center;
  overflow: hidden;
}
.cc-hero-avatar img { width: 100%; height: 100%; object-fit: cover; }
@media (max-width: 640px) {
  .cc-hero { padding: 18px 16px; }
  .cc-hero-avatar { width: 72px; height: 72px; border-radius: 18px; }
}

/* ── Search field ─────────────────────────────────────────────────── */
.cc-search-wrap {
  position: relative; display: flex; align-items: center;
  background: #ffffff;
  border-radius: 18px;
  padding: 4px 4px 4px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.28);
}
.cc-search-icon { color: #6b7280; flex-shrink: 0; }
.cc-search-input {
  flex: 1;
  background: none; border: none; outline: none;
  color: #0f172a; font-size: 15px; font-weight: 500;
  padding: 14px 10px; min-width: 0;
  font-family: inherit;
}
.cc-search-input::placeholder { color: #94a3b8; font-weight: 500; }
.cc-search-clear {
  flex-shrink: 0; margin-right: 4px;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(148,163,184,0.15); color: #64748b;
  border: none; cursor: pointer; font-family: inherit;
  display: grid; place-items: center;
}
.cc-search-clear:hover { background: rgba(148,163,184,0.30); color: #0f172a; }
.cc-search-count {
  padding: 8px 12px;
  color: #64748b; font-size: 12px; font-weight: 700;
  border-left: 1px solid rgba(148,163,184,0.28);
}

/* ── Section header ──────────────────────────────────────────────── */
.cc-section-head {
  display: flex; flex-direction: column; gap: 3px;
  padding-bottom: 4px;
}
.cc-section-eyebrow {
  font-size: 11px; font-weight: 800;
  letter-spacing: 0.20em; text-transform: uppercase;
  color: #94a3b8;
}
.cc-section-title {
  margin: 0;
  font-size: 20px; font-weight: 800; letter-spacing: -0.01em;
  color: #f1f5f9;
}
.cc-section-title-underline {
  display: block; width: 42px; height: 3px;
  margin-top: 6px;
  border-radius: 3px;
  background: #facc15;
}
.cc-section-sub {
  color: #94a3b8; font-size: 13px; margin: 2px 0 0;
  line-height: 1.4;
}

/* ── Tool grid ──────────────────────────────────────────────────── */
.cc-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
@media (min-width: 700px) {
  .cc-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
}
@media (min-width: 1020px) {
  .cc-grid { grid-template-columns: repeat(3, 1fr); gap: 18px; }
}

/* Featured (primary recommendation) spans two columns on desktop */
.cc-grid-featured { grid-column: span 1; }
@media (min-width: 1020px) {
  .cc-grid-featured { grid-column: span 2; }
}

/* ── Tool card ──────────────────────────────────────────────────── */
.cc-card {
  position: relative;
  display: flex; flex-direction: column; gap: 12px;
  min-height: 168px;
  padding: 18px;
  background: rgba(15, 27, 70, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  color: #f1f5f9;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  transition: transform 160ms, border-color 180ms, background 180ms, box-shadow 180ms;
}
.cc-card:hover, .cc-card:focus-visible {
  transform: translateY(-3px);
  border-color: rgba(250, 204, 21, 0.55);
  background: rgba(15, 27, 70, 0.95);
  box-shadow: 0 14px 30px rgba(0,0,0,0.35);
  outline: none;
}
.cc-card:focus-visible {
  border-color: #facc15;
  box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.35), 0 14px 30px rgba(0,0,0,0.35);
}

.cc-card-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: grid; place-items: center;
  color: var(--cc-c, #67e8f9);
  background: color-mix(in oklab, var(--cc-c, #67e8f9) 14%, transparent);
  border: 1px solid color-mix(in oklab, var(--cc-c, #67e8f9) 42%, transparent);
  flex-shrink: 0;
}
.cc-card-title {
  margin: 0;
  font-size: 16px; font-weight: 800; letter-spacing: -0.005em;
  color: #f1f5f9;
}
.cc-card-desc {
  color: #cbd5e1;
  font-size: 13px; line-height: 1.5;
  font-weight: 500;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.cc-card-cta {
  margin-top: auto;
  display: inline-flex; align-items: center; gap: 6px;
  color: var(--cc-c, #67e8f9);
  font-size: 13px; font-weight: 700;
  letter-spacing: 0.02em;
}
.cc-card-badge {
  position: absolute; top: 12px; right: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(250, 204, 21, 0.18);
  color: #facc15;
  border: 1px solid rgba(250, 204, 21, 0.45);
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Featured card visual emphasis */
.cc-card-featured {
  background: linear-gradient(135deg, rgba(15, 27, 70, 0.95) 0%, rgba(29, 78, 216, 0.35) 100%);
  border-color: rgba(103, 232, 249, 0.35);
  min-height: 180px;
}
.cc-card-featured .cc-card-title { font-size: 20px; }
.cc-card-featured .cc-card-desc { -webkit-line-clamp: 3; font-size: 14px; }

/* ── Continue Work section ──────────────────────────────────────── */
.cc-continue {
  display: flex; flex-direction: column; gap: 8px;
}
.cc-continue-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  background: rgba(15, 27, 70, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  cursor: pointer; font-family: inherit;
  text-decoration: none; color: #f1f5f9;
  transition: background 160ms, border-color 160ms;
}
.cc-continue-item:hover { background: rgba(15, 27, 70, 0.85); border-color: rgba(250, 204, 21, 0.35); }
.cc-continue-icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: grid; place-items: center;
  color: #facc15; background: rgba(250, 204, 21, 0.12);
  border: 1px solid rgba(250, 204, 21, 0.30);
  flex-shrink: 0;
}
.cc-continue-body { flex: 1; min-width: 0; }
.cc-continue-title { font-size: 14px; font-weight: 700; color: #f1f5f9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cc-continue-meta { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.cc-continue-btn {
  padding: 6px 12px; border-radius: 8px;
  background: #facc15; color: #0d1438;
  font-size: 12px; font-weight: 800;
  border: none; flex-shrink: 0;
  cursor: pointer; font-family: inherit;
}

/* Mobile: stack the continue item so the Continue chip doesn't collide
   with long draft titles. */
@media (max-width: 520px) {
  .cc-continue-item {
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .cc-continue-body { flex-basis: calc(100% - 48px); }
  .cc-continue-btn  {
    margin-left: 48px;
    width: 100%;
    padding: 8px 12px;
    text-align: center;
  }
}

/* ── Quick help panel ───────────────────────────────────────────── */
.cc-quickhelp {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(15, 27, 70, 0.85) 0%, rgba(29, 78, 216, 0.28) 100%);
  border: 1px solid rgba(103, 232, 249, 0.28);
  flex-wrap: wrap;
}
.cc-quickhelp-icon {
  width: 48px; height: 48px; border-radius: 14px;
  color: #67e8f9; background: rgba(103, 232, 249, 0.12);
  border: 1px solid rgba(103, 232, 249, 0.35);
  display: grid; place-items: center; flex-shrink: 0;
}
.cc-quickhelp-body { flex: 1; min-width: 200px; }
.cc-quickhelp-title { font-size: 16px; font-weight: 800; color: #f1f5f9; margin: 0; }
.cc-quickhelp-sub { font-size: 13px; color: #cbd5e1; margin: 4px 0 0; line-height: 1.4; }
.cc-quickhelp-btn {
  padding: 10px 16px; border-radius: 12px;
  background: #67e8f9; color: #0d1438;
  border: none; cursor: pointer; font-family: inherit;
  font-size: 13px; font-weight: 800; letter-spacing: 0.02em;
  flex-shrink: 0;
  transition: background 160ms, transform 160ms;
}
.cc-quickhelp-btn:hover { background: #22d3ee; transform: translateY(-1px); }

/* ── Skeleton / empty / error states ────────────────────────────── */
.cc-skeleton-card {
  min-height: 168px;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(15,27,70,0.55) 0%, rgba(15,27,70,0.85) 50%, rgba(15,27,70,0.55) 100%);
  background-size: 200% 100%;
  animation: cc-shimmer 1.6s infinite linear;
  border: 1px solid rgba(148,163,184,0.14);
}
@keyframes cc-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) {
  .cc-skeleton-card { animation: none; }
}
.cc-empty {
  padding: 40px 20px;
  text-align: center;
  border-radius: 16px;
  background: rgba(15, 27, 70, 0.55);
  border: 1px dashed rgba(148, 163, 184, 0.30);
}
.cc-empty h3 { margin: 0 0 6px; font-size: 16px; font-weight: 800; color: #f1f5f9; }
.cc-empty p { margin: 0 0 14px; font-size: 13.5px; color: #cbd5e1; }
.cc-empty button {
  padding: 10px 20px; border-radius: 12px;
  background: #facc15; color: #0d1438;
  border: none; cursor: pointer; font-family: inherit;
  font-size: 13px; font-weight: 800;
}
`;
